import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Row,
  Table,
  Input,
  ButtonGroup,
  Button,
  FormGroup,
  Label,
} from "reactstrap";
import AppModal from "../../../../Components/Common/AppModal";
import SettingsPanel from "../../components/SettingsPanel";
import {
  authProtectedRoutes,
  publicRoutes,
} from "../../../../Routes/allRoutes";
import { useDispatch, useSelector } from "react-redux";
import {
  createMasterMenuNavItem,
  createMasterMenuNavItemSuccess,
  getAvailableMenuNavigationItems,
} from "../../../../store/navigationMenu/action";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";

// --- MOCK ICON DATABASE ---
const COMMON_ICONS = [
  "ri-dashboard-line",
  "ri-settings-3-line",
  "ri-settings-4-line",
  "ri-search-line",
  "ri-zoom-in-line",
  "ri-zoom-out-line",
  "ri-filter-3-line",
  "ri-equalizer-line",
  "ri-menu-line",
  "ri-more-2-line",
  "ri-more-fill",
  "ri-star-line",
  "ri-star-fill",
  "ri-heart-line",
  "ri-heart-fill",
  "ri-thumb-up-line",
  "ri-thumb-down-line",
  "ri-notification-3-line",
  "ri-notification-badge-line",
  "ri-eye-line",
  "ri-eye-off-line",
  "ri-lock-line",
  "ri-lock-unlock-line",
  "ri-key-line",
  "ri-shield-check-line",
  "ri-delete-bin-line",
  "ri-edit-2-line",
  "ri-pencil-line",
  "ri-save-line",
  "ri-download-line",
  "ri-upload-line",
  "ri-share-line",
  "ri-external-link-line",
  "ri-refresh-line",
  "ri-time-line",
  "ri-calendar-line",
  "ri-calendar-check-line",
  "ri-user-line",
  "ri-user-add-line",
  "ri-user-settings-line",
  "ri-team-line",
  "ri-group-line",
  "ri-contacts-book-2-line",
  "ri-mail-line",
  "ri-mail-send-line",
  "ri-message-line",
  "ri-message-2-line",
  "ri-chat-1-line",
  "ri-discuss-line",
  "ri-phone-line",
  "ri-customer-service-2-line",
  "ri-briefcase-line",
  "ri-bar-chart-line",
  "ri-pie-chart-line",
  "ri-line-chart-line",
  "ri-funds-line",
  "ri-wallet-line",
  "ri-bank-card-line",
  "ri-money-dollar-circle-line",
  "ri-exchange-dollar-line",
  "ri-shopping-cart-line",
  "ri-shopping-bag-line",
  "ri-store-2-line",
  "ri-store-3-line",
  "ri-restaurant-line",
  "ri-cup-line",
  "ri-price-tag-3-line",
  "ri-ticket-2-line",
  "ri-file-list-line",
  "ri-file-text-line",
  "ri-file-add-line",
  "ri-folder-line",
  "ri-folder-add-line",
  "ri-folder-open-line",
  "ri-clipboard-line",
  "ri-book-read-line",
  "ri-article-line",
  "ri-newspaper-line",
  "ri-image-line",
  "ri-camera-line",
  "ri-video-line",
  "ri-play-circle-line",
  "ri-music-2-line",
  "ri-mic-line",
  "ri-computer-line",
  "ri-macbook-line",
  "ri-smartphone-line",
  "ri-tablet-line",
  "ri-tv-2-line",
  "ri-printer-line",
  "ri-map-pin-line",
  "ri-earth-line",
  "ri-global-line",
  "ri-navigation-line",
  "ri-car-line",
  "ri-truck-line",
  "ri-flight-takeoff-line",
  "ri-building-4-line",
  "ri-home-4-line",
  "ri-hospital-line",
  "ri-record-circle-line",
  "ri-checkbox-circle-line",
  "ri-close-circle-line",
  "ri-add-circle-line",
  "ri-information-line",
  "ri-question-line",
  "ri-error-warning-line",
  "ri-arrow-right-line",
  "ri-arrow-left-line",
  "ri-arrow-up-line",
  "ri-arrow-down-line",
  "ri-arrow-right-circle-line",
  "ri-arrow-left-circle-line",
  "ri-check-line",
  "ri-close-line",
  "ri-add-line",
];

// --- HELPER: AUTO-GENERATE MENU NAME ---
// Task 24679 - stored paths carry NO leading slash. Every one of the 56 rows
// in MenuNavOption is "tasks", "dashboard", "customer-list", and
// AuthLoginController builds the landing URL as '/' . Url - so a stored
// "/my-day" comes back as "//my-day".
const normaliseMenuUrl = (path) =>
  String(path || "").trim().replace(/^\/+|\/+$/g, "");

const generateMenuName = (path) => {
  if (!path || path === "/") return "Home";
  const cleanPath = path.replace(/^\/+|\/+$/g, "");
  const segments = cleanPath
    .split("/")
    .filter((seg) => !seg.startsWith(":") && !seg.includes("{{"));
  const targetSegment = segments.pop() || "Menu Item";

  return targetSegment
    .replace(/[-_]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};


// Change #100339 - a ":param" segment (or "{{...}}") still in a menu address.
const PARAM_SEGMENT_RE = /(^|\/):([A-Za-z_][\w]*)\??(?=\/|$)|\{\{[^}]*\}\}/;
const hasUnfilledParam = (url = "") => PARAM_SEGMENT_RE.test(String(url || ""));
const unfilledParamName = (url = "") => {
  const m = String(url || "").match(PARAM_SEGMENT_RE);
  return (m && (m[2] || m[0].replace(/[{}]/g, ""))) || "id";
};

const isDynamicRoutePath = (path = "") => {
  return path.includes(":") || path.includes("{{") || path.includes("}");
};

const getClickablePath = (path = "") => {
  if (!path) return "#";

  // Prevent bad navigation for routes like /pos/:id
  if (isDynamicRoutePath(path)) return null;

  return path.startsWith("/") ? path : `/${path}`;
};
const SuperMenuSettings = () => {
  const dispatch = useDispatch();

  // Route Filters State
  const [searchTerm, setSearchTerm] = useState("");
  const [routeFilter, setRouteFilter] = useState("All");
  const [menuFilter, setMenuFilter] = useState("All");

  // --- MODAL & ICON PICKER STATE ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuFormData, setMenuFormData] = useState({
    url: "",
    name: "",
    icon: "ri-record-circle-line",
  });

  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconSearchTerm, setIconSearchTerm] = useState("");
  const [visibleIconCount, setVisibleIconCount] = useState(60);

  useEffect(() => {
    dispatch(getAvailableMenuNavigationItems());
  }, [dispatch]);

  const rawMenuData = useSelector(
    (state) => state.NavigationMenu?.availableMenuNavigationItems
  );

  const menuItemsList = Array.isArray(rawMenuData?.menunavoption_list)
    ? rawMenuData.menunavoption_list
    : Array.isArray(rawMenuData)
    ? rawMenuData
    : [];

  const allCombinedRoutes = [
    ...authProtectedRoutes.map((r) => ({ ...r, routeType: "Private" })),
    ...publicRoutes.map((r) => ({ ...r, routeType: "Public" })),
  ];

  const checkMatch = (routePath, apiUrl) => {
    if (!routePath || !apiUrl) return false;
    const normRoute = routePath
      .toLowerCase()
      .trim()
      .replace(/^\/+|\/+$/g, "");
    const normApi = apiUrl
      .toLowerCase()
      .trim()
      .replace(/^\/+|\/+$/g, "");
    if (normRoute === normApi) return true;
    const baseRoute = normRoute.split("/:")[0];
    const baseApi = normApi.split(/\/\{\{/)[0];
    if (baseRoute === baseApi) return true;
    if (normApi.startsWith(baseRoute + "/")) return true;
    return false;
  };

  const processedRoutes = allCombinedRoutes
    .map((route) => {
      const matchedMenu = menuItemsList.find((menu) =>
        checkMatch(route.path, menu.UrlReact)
      );
      return { ...route, matchedMenu, hasMenu: !!matchedMenu };
    })
    .filter((route) => {
      if (
        searchTerm &&
        !route.path.toLowerCase().includes(searchTerm.toLowerCase())
      )
        return false;
      if (routeFilter !== "All" && route.routeType !== routeFilter)
        return false;
      if (menuFilter === "HasMenu" && !route.hasMenu) return false;
      if (menuFilter === "NoMenu" && route.hasMenu) return false;
      return true;
    });

  // --- MODAL HANDLERS ---
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    setShowIconPicker(false);
    setIconSearchTerm("");
  };

  const openCreateModal = (route) => {
    setMenuFormData({
      url: normaliseMenuUrl(route.path),
      name: generateMenuName(route.path),
      icon: "ri-record-circle-line",
    });
    setIsModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setMenuFormData((prev) => ({ ...prev, [name]: value }));
  };

  // --- ICON PICKER LOGIC ---
  const filteredIcons = COMMON_ICONS.filter((icon) =>
    icon.toLowerCase().includes(iconSearchTerm.toLowerCase().trim())
  );

  const handleIconScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 50;
    if (bottom && visibleIconCount < filteredIcons.length) {
      setVisibleIconCount((prevCount) => prevCount + 60);
    }
  };

  const selectIcon = (iconClass) => {
    setMenuFormData((prev) => ({ ...prev, icon: iconClass }));
    setShowIconPicker(false);
  };

  useEffect(() => {
    setVisibleIconCount(60);
  }, [iconSearchTerm]);

  // --- SUBMIT HANDLER ---
  const handleCreateMenu = () => {
    // 1. Format payload to match the Postman API exactly
    // Task 24679 - UrlReact was never sent, and the column is NOT NULL, so
    // every create from this screen died with "Column 'UrlReact' cannot be
    // null". The controller has always accepted the field; the form just did
    // not supply it.
    //
    // It is also the column this screen MATCHES on (checkMatch reads
    // menu.UrlReact), so even a successful insert without it would have left
    // the route showing as "no menu" forever.
    const cleanUrl = normaliseMenuUrl(menuFormData.url);

    // Change #100339 - "this had :id as its dynamic so i changed it to 1".
    // A route pattern saved as a menu address is a dead link in every company
    // it is copied to, so it is refused until the parameter is filled in.
    if (hasUnfilledParam(cleanUrl)) {
      toast.error(
        `Replace :${unfilledParamName(cleanUrl)} in the URL with a real value first`
      );
      return;
    }

    const formattedPayload = [
      {
        Name: menuFormData.name,
        Icon: menuFormData.icon,
        Url: cleanUrl,
        // The same value for both, which is what all 56 existing rows do.
        // They only diverge for routes with parameters, where UrlReact carries
        // the {{...}} placeholders the matcher strips.
        UrlReact: cleanUrl,
      },
    ];

    dispatch(createMasterMenuNavItem(formattedPayload));
  };

  // --- REDUX SUCCESS HANDLING ---
  const { createMasterMenuNavItemSuccessAPI } = useSelector((state) => ({
    createMasterMenuNavItemSuccessAPI:
      state.NavigationMenu?.createMasterMenuNavItemSuccess,
  }));

  useEffect(() => {
    if (isEmpty(createMasterMenuNavItemSuccessAPI)) return;

    if (createMasterMenuNavItemSuccessAPI?.status === 200) {
      dispatch(getAvailableMenuNavigationItems());
      toast.success(
        createMasterMenuNavItemSuccessAPI?.message ||
          "Menu item created successfully"
      );
      toggleModal();
    } else {
      toast.error(
        createMasterMenuNavItemSuccessAPI?.message || "Error creating menu item"
      );
    }

    // 2. Properly clear the success state via dispatch
    dispatch(createMasterMenuNavItemSuccess([]));
  }, [createMasterMenuNavItemSuccessAPI, dispatch]);

  // Change #100338 - "can i have a button to readd delta?"
  const [queueingRollout, setQueueingRollout] = useState(false);
  const [queuedDeltaId, setQueuedDeltaId] = useState(null);
  const handleQueueRollout = async () => {
    setQueueingRollout(true);
    try {
      const res = await FetchAPI("/menunavoption/queuerollout", "POST", {});
      if (res?.status === 200) {
        setQueuedDeltaId(res.DatabaseDelta_Id || null);
        toast.success(res.message || "Rollout delta queued");
      } else {
        toast.error(res?.message || "Could not queue the rollout delta");
      }
    } catch (e) {
      toast.error(e?.message || "Could not queue the rollout delta");
    } finally {
      setQueueingRollout(false);
    }
  };

  return (
    <React.Fragment>
      <SettingsPanel
        title="Super Menu"
        description="Map application routes to master navigation menu items."
      >
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
                  <h4 className="card-title mb-0">Super Admin Menu Management</h4>
                  {/* Change #100338 - a new option only reaches a company's
                      Settings > Menus once it is copied into that company's
                      MenuNavItem. This queues the copy as a delta; running it
                      stays a deliberate click in Database Deltas. */}
                  <div className="text-end">
                    <Button
                      color="warning"
                      size="sm"
                      disabled={queueingRollout}
                      onClick={handleQueueRollout}
                      title="Register a delta that copies new menu options into every company"
                    >
                      <i className="ri-database-2-line align-bottom me-1" />
                      {queueingRollout ? "Queueing…" : "Queue rollout delta"}
                    </Button>
                    <div className="text-muted fs-11 mt-1">
                      Then run it in{" "}
                      <Link to="/settings/3/14">Database Deltas</Link>
                      {queuedDeltaId ? ` (delta #${queuedDeltaId})` : ""}
                    </div>
                  </div>
                </div>

                {/* --- FILTERS SECTION --- */}
                <Row className="align-items-center mb-4">
                  <Col md={4} className="mb-2 mb-md-0">
                    <Input
                      type="text"
                      placeholder="Search path..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Col>
                  <Col md={8} className="d-flex gap-2 flex-wrap">
                    <ButtonGroup>
                      <Button
                        color={
                          routeFilter === "All" ? "primary" : "outline-primary"
                        }
                        onClick={() => setRouteFilter("All")}
                      >
                        All Routes
                      </Button>
                      <Button
                        color={
                          routeFilter === "Private"
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() => setRouteFilter("Private")}
                      >
                        Private
                      </Button>
                      <Button
                        color={
                          routeFilter === "Public"
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() => setRouteFilter("Public")}
                      >
                        Public
                      </Button>
                    </ButtonGroup>
                    <ButtonGroup>
                      <Button
                        color={menuFilter === "All" ? "info" : "outline-info"}
                        onClick={() => setMenuFilter("All")}
                      >
                        All Items
                      </Button>
                      <Button
                        color={
                          menuFilter === "HasMenu" ? "info" : "outline-info"
                        }
                        onClick={() => setMenuFilter("HasMenu")}
                      >
                        Has Button
                      </Button>
                      <Button
                        color={
                          menuFilter === "NoMenu" ? "info" : "outline-info"
                        }
                        onClick={() => setMenuFilter("NoMenu")}
                      >
                        Missing Button
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>

                {/* --- TABLE SECTION --- */}
                <div className="table-responsive">
                  <Table className="table table-striped table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Path</th>
                        <th>Type</th>
                        <th>Menu Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedRoutes.length > 0 ? (
                        processedRoutes.map((route, idx) => (
                          <tr key={idx}>
                            <td>
                              {getClickablePath(route.path) ? (
                                <a
                                  href={getClickablePath(route.path)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="fw-medium text-primary text-decoration-none d-inline-flex align-items-center gap-1"
                                  title={`Open ${route.path}`}
                                >
                                  <i className="ri-external-link-line align-middle"></i>
                                  <span>{route.path}</span>
                                </a>
                              ) : (
                                <span
                                  className="text-muted d-inline-flex align-items-center gap-1"
                                  title="This route contains dynamic parameters and cannot be opened directly"
                                >
                                  <i className="ri-lock-line align-middle"></i>
                                  {route.path}
                                </span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  route.routeType === "Private"
                                    ? "bg-danger"
                                    : "bg-success"
                                }`}
                              >
                                {route.routeType}
                              </span>
                            </td>
                            <td>
                              {route.hasMenu ? (
                                // Task 24884 - show WHICH menu item matched, by
                                // Id. "Menu Exists" alone is a dead end: the
                                // next thing you want is to go and look at it,
                                // and MenuNavOption.Id is how you find it.
                                <div className="text-success fw-medium d-inline-flex align-items-center gap-2 flex-wrap">
                                  <span>
                                    <i
                                      className={`${
                                        route.matchedMenu?.Icon ||
                                        "ri-record-circle-line"
                                      } me-1 align-middle`}
                                    ></i>
                                    Menu Exists
                                  </span>
                                  {route.matchedMenu?.Name ? (
                                    <span className="text-muted fw-normal">
                                      {route.matchedMenu.Name}
                                    </span>
                                  ) : null}
                                  {route.matchedMenu?.Id ? (
                                    <span
                                      className="badge bg-light text-dark border fw-normal"
                                      title="MenuNavOption Id - quote this when asking about this menu item"
                                    >
                                      #{route.matchedMenu.Id}
                                    </span>
                                  ) : null}
                                </div>
                              ) : (
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => openCreateModal(route)}
                                >
                                  <i className="ri-add-line align-middle me-1"></i>
                                  Create new menu item
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted">
                            No routes found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </SettingsPanel>

      {/* --- CREATE MENU ITEM MODAL --- */}
      <AppModal
        open={isModalOpen}
        onClose={toggleModal}
        title="Create New Menu Item"
        headerAccent="indigo"
        size="sm"
        onSubmit={handleCreateMenu}
        submitLabel="Create Menu Item"
        submitColor="primary"
      >
        <FormGroup className="mb-3">
            <Label htmlFor="menuUrl">URL Path</Label>
            <Input
              type="text"
              id="menuUrl"
              name="url"
              value={menuFormData.url}
              onChange={handleFormChange}
              invalid={hasUnfilledParam(menuFormData.url)}
            />
            {/* Change #100339 */}
            {hasUnfilledParam(menuFormData.url) && (
              <div className="text-danger small mt-1">
                Replace the <code>:{unfilledParamName(menuFormData.url)}</code>{" "}
                part with a real value (e.g. <code>field-service-dispatch/1</code>
                ) - a menu link cannot open a route pattern. The value is copied
                to every company as-is, so pick one that exists everywhere, or
                set it per company in Settings &gt; Menus.
              </div>
            )}
          </FormGroup>

          <FormGroup className="mb-3">
            <Label htmlFor="menuName">Menu Name</Label>
            <Input
              type="text"
              id="menuName"
              name="name"
              value={menuFormData.name}
              onChange={handleFormChange}
              placeholder="e.g., Settings, Dashboard"
            />
          </FormGroup>

          {/* --- ENHANCED ICON PICKER --- */}
          <FormGroup className="mb-3 position-relative">
            <Label>Icon</Label>
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center bg-light border rounded"
                style={{ width: "45px", height: "45px" }}
              >
                <i className={`${menuFormData.icon} fs-3 text-primary`}></i>
              </div>

              <Button
                color="light"
                className="border"
                onClick={() => setShowIconPicker(!showIconPicker)}
              >
                {showIconPicker ? "Close Icon Picker" : "Browse Icons"}
              </Button>
            </div>

            {showIconPicker && (
              <div
                className="border rounded mt-2 p-3 bg-white shadow-lg"
                style={{
                  position: "absolute",
                  zIndex: 1050,
                  width: "100%",
                  top: "100%",
                  left: 0,
                }}
              >
                <Input
                  type="text"
                  placeholder="Search icons (e.g., map, user, file)..."
                  value={iconSearchTerm}
                  onChange={(e) => setIconSearchTerm(e.target.value)}
                  className="mb-3"
                  autoFocus
                />

                <div
                  className="d-flex flex-wrap gap-2 justify-content-start"
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    paddingRight: "5px",
                  }}
                  onScroll={handleIconScroll}
                >
                  {filteredIcons.slice(0, visibleIconCount).map((iconClass) => (
                    <Button
                      key={iconClass}
                      color="light"
                      className={`btn-icon border ${
                        menuFormData.icon === iconClass
                          ? "border-primary text-primary bg-primary bg-opacity-10"
                          : ""
                      }`}
                      style={{ width: "42px", height: "42px", padding: 0 }}
                      title={iconClass}
                      onClick={() => selectIcon(iconClass)}
                    >
                      <i className={`${iconClass} fs-5`}></i>
                    </Button>
                  ))}

                  {filteredIcons.length === 0 && (
                    <div className="text-muted small w-100 text-center py-4">
                      <i className="ri-search-line fs-3 d-block mb-2 text-light"></i>
                      No icons found matching "{iconSearchTerm}"
                    </div>
                  )}
                </div>

                <div className="text-end mt-2">
                  <small className="text-muted fw-medium">
                    Showing {Math.min(visibleIconCount, filteredIcons.length)}{" "}
                    of {filteredIcons.length}
                  </small>
                </div>
              </div>
            )}
          </FormGroup>
      </AppModal>
    </React.Fragment>
  );
};

export default SuperMenuSettings;
